import { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  useToast,
  Text,
  FormLabel,
  FormControl,
  Select,
  HStack,
  Button,
} from "@chakra-ui/react";
import { BrandButton } from "../BrandButton/BrandButton";
import { getAxiosClient } from "../../services/HttpsService";
import { useDispatch } from "react-redux";
import { fetchApplications } from "../../services/state/applications/applications-async-calls";
import { AppDispatch } from "@/services/state/store";
import { Application } from "@/services/state/applications/applications-interface";
import { fetchDataSources } from "../../services/state/administration/administration-async-calls";

export const AddDataSourceModal = ({
  isOpen,
  onClose,
  edit = false,
  application,
}: {
  isOpen: boolean;
  onClose: () => void;
  edit?: boolean;
  application?: Application;
}) => {
  const initialRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();

  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [connString, setConnString] = useState("");
  const [loading, setLoading] = useState(false);

  // useEffect(() => {
  //   if (edit && application) {
  //     setName(application.appName);
  //     setDescription(application.appDesc);
  //     setPrefix(application.appPrefix);
  //   }
  // }, [edit, application]);

  const onCreate = async () => {
    if (
      name !== "" &&
      username !== "" &&
      password !== "" &&
      connString !== ""
    ) {
      setLoading(true);
      const url = "/api/eza_sys_admin_datasource_config";
      const data = {
        dbSourceNm: name,
        databaseCd: "MY_SQL",
        dbUser: username,
        dbPwd: password,
        dbConnString: connString,
      };

      try {
        const response = await getAxiosClient().post(url, data);

        if (response.status === 201) {
          toast({
            title: "Successfully created new data source",
            description: "Created new data source with entered details",
            position: "top",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          onClose();
          dispatch(fetchDataSources());
          setName("");
          setUsername("");
          setPassword("");
          setConnString("");
        } else {
          toast({
            title: "Some error has occurred",
            description: "Please try again",
            position: "top",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error creating tenant:", error);
        toast({
          title: "Error",
          description: "An error occurred while creating new data source.",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    } else {
      toast({
        title: "All the details are required",
        description: "Please enter all the details and try again",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const onTestConnection = async () => {
    if (
      name !== "" &&
      username !== "" &&
      password !== "" &&
      connString !== ""
    ) {
      setLoading(true);
      const data = {
        username: username,
        password: password,
        url: connString,
      };

      try {
        const response = await fetch(
          (process.env.REACT_APP_RULE_ENGINE_URL as string) +
            "/test-connection",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
          }
        );

        if (response.status === 200 && (await response.json())) {
          toast({
            title: "Successfully connected to data source",
            description:
              "Able to connect to the data source with entered details",
            position: "top",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        } else {
          toast({
            title: "Some error has occurred",
            description:
              "Unable to connect to the data source with entered details",
            position: "top",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error creating tenant:", error);
        toast({
          title: "Error",
          description:
            "Unable to connect to the data source with entered details",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    } else {
      toast({
        title: "All the details are required",
        description: "Please enter all the details and try again",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      initialFocusRef={initialRef}
      size="xl"
      isCentered
    >
      <ModalOverlay
        zIndex={1}
        backdropFilter="blur(10px)" // ← This will blur the page behind the modal
        bg="rgba(0, 0, 0, 0.4)"
      />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody>
          {edit ? (
            <Text fontSize="20px" fontWeight="semibold" my={4}>
              Edit Data Source
            </Text>
          ) : (
            <Text fontSize="20px" fontWeight="semibold" my={4}>
              Configure Data Source
            </Text>
          )}
          <FormControl isRequired>
            <FormLabel>Name</FormLabel>
            <Input
              placeholder="Enter name of the Data Source"
              focusBorderColor="brand.600"
              ref={initialRef}
              fontSize="18px"
              value={name}
              onChange={(e) => setName(e.target.value)}
              overflowX="auto"
              isRequired
            />
          </FormControl>
          <FormControl mt={3}>
            <FormLabel>Username</FormLabel>
            <Input
              placeholder="Enter username"
              focusBorderColor="brand.600"
              fontSize="18px"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              overflowX="auto"
              isRequired
            />
          </FormControl>
          <FormControl mt={3}>
            <FormLabel>Password</FormLabel>
            <Input
              type="password"
              placeholder="Enter password"
              focusBorderColor="brand.600"
              fontSize="18px"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              overflowX="auto"
            />
          </FormControl>
          <FormControl mt={3}>
            <FormLabel>Type</FormLabel>
            <Select
              placeholder="Select option"
              value="MY_SQL"
              focusBorderColor="brand.600"
            >
              <option value="MY_SQL">MySQL</option>
            </Select>
          </FormControl>
          <FormControl mt={3}>
            <FormLabel>Connection String</FormLabel>
            <Input
              placeholder="Enter connection string of data source"
              focusBorderColor="brand.600"
              fontSize="18px"
              value={connString}
              onChange={(e) => setConnString(e.target.value)}
              overflowX="auto"
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button
              onClick={onTestConnection}
              isLoading={loading}
              isDisabled={loading}
              colorScheme="brand"
              variant="outline"
              size="lg"
            >
              Test Connection
            </Button>
            {edit ? (
              <BrandButton
                // onClick={onUpdate}
                isLoading={loading}
                isDisabled={loading}
              >
                Save Changes
              </BrandButton>
            ) : (
              <BrandButton
                onClick={onCreate}
                isLoading={loading}
                isDisabled={loading}
              >
                Create
              </BrandButton>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
